import { Link } from "react-router-dom";
import {
  Droplet, Activity, HeartPulse, CalendarCheck2, ShieldAlert, ArrowRight,
  UtensilsCrossed, Syringe, FileQuestion,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const Disclaimer = () => (
  <div
    data-testid="medical-disclaimer"
    role="note"
    className="flex items-start gap-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 sm:p-6"
  >
    <ShieldAlert size={26} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
    <p className="text-sm sm:text-base font-semibold leading-relaxed text-amber-900">
      Ces informations sont générales et ne remplacent pas l'avis d'un médecin. Pour toute
      question sur votre santé ou celle d'un proche, consultez un professionnel de santé.
    </p>
  </div>
);

const Maladie = () => (
  <div data-testid="maladie-page">
    <PageHero
      eyebrow="Comprendre"
      title="La maladie de Waldmann, expliquée simplement"
      description="Aussi appelée lymphangiectasie intestinale primitive, c'est une maladie chronique rare des vaisseaux lymphatiques de l'intestin. Voici l'essentiel, en mots simples."
      testid="maladie-title"
    />

    <section className="py-16 md:py-24">
      <div className="container-site max-w-4xl">
        <Disclaimer />

        <Reveal className="mt-16">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#0284C7]">
              <FileQuestion size={21} />
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
              Qu'est-ce que c'est ?
            </h2>
          </div>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Dans l'intestin circulent de petits vaisseaux lymphatiques qui transportent la lymphe,
            un liquide riche en protéines et en globules blancs (nos défenses immunitaires). Dans
            la maladie de Waldmann, ces vaisseaux sont anormalement dilatés : la lymphe fuit dans
            le tube digestif au lieu de retourner dans le sang.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Résultat : l'organisme perd en permanence des protéines essentielles, notamment les
            immunoglobulines qui protègent des infections. C'est une maladie chronique rare —
            environ une personne sur un million est concernée — qui peut toucher les enfants
            comme les adultes.
          </p>
        </Reveal>

        <Reveal className="mt-14">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#0284C7]">
              <Activity size={21} />
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
              Les symptômes courants
            </h2>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              "Œdèmes (gonflements) des jambes, du visage ou d'autres parties du corps",
              "Troubles digestifs : diarrhées, douleurs ou ballonnements",
              "Fatigue importante et persistante",
              "Infections à répétition, liées à la baisse des défenses immunitaires",
              "Baisse des protéines dans le sang (visible à la prise de sang)",
              "Retard de croissance possible chez l'enfant",
            ].map((s, i) => (
              <div key={i} className="card-site flex items-start gap-3 !p-5" data-testid={`symptom-${i}`}>
                <Droplet size={18} className="mt-1 shrink-0 text-[#FF6B6B]" />
                <p className="text-sm sm:text-base font-medium text-slate-700">{s}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-slate-500">
            Ces symptômes peuvent avoir de nombreuses autres causes. Seul un médecin peut poser un
            diagnostic, généralement après des examens spécialisés (prise de sang, endoscopie,
            imagerie).
          </p>
        </Reveal>

        <Reveal className="mt-14">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#0284C7]">
              <Syringe size={21} />
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
              Les traitements
            </h2>
          </div>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Il n'existe pas de traitement qui guérit la maladie, mais une prise en charge efficace
            permet de vivre avec :
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Syringe, title: "Perfusions régulières", text: "Apports d'immunoglobulines ou d'albumine pour compenser les pertes, souvent chaque semaine." },
              { icon: UtensilsCrossed, title: "Alimentation adaptée", text: "Régime pauvre en graisses à longue chaîne, avec des graisses spécifiques (TCM) mieux tolérées." },
              { icon: HeartPulse, title: "Suivi des complications", text: "Surveillance des œdèmes, de l'immunité et des possibles atteintes respiratoires." },
            ].map((t, i) => (
              <div key={i} className="card-site h-full p-6" data-testid={`treatment-${i}`}>
                <t.icon size={24} className="text-[#0284C7]" />
                <h3 className="mt-4 font-heading text-lg font-semibold text-[#0F2C59]">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.text}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <div className="rounded-[2rem] bg-[#0F2C59] p-8 sm:p-12">
            <div className="flex items-center gap-3">
              <CalendarCheck2 size={26} className="text-[#7DD3FC]" />
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Pourquoi le suivi est si important
              </h2>
            </div>
            <p className="mt-5 text-base leading-relaxed text-sky-100/85">
              La maladie de Waldmann évolue par périodes : des phases stables, et des poussées où
              les pertes s'aggravent. Un suivi hospitalier régulier — souvent hebdomadaire —
              permet d'ajuster les perfusions, de dépister les complications tôt et de préserver
              la qualité de vie. Ne jamais interrompre ce suivi sans avis médical est essentiel.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/suivi-medical" data-testid="maladie-suivi-link" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#0F2C59] transition-[transform,background-color] duration-200 hover:-translate-y-0.5">
                Les hôpitaux qui connaissent la maladie <ArrowRight size={16} />
              </Link>
              <Link to="/vivre-avec" data-testid="maladie-vivre-link" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10">
                Vivre avec au quotidien
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <div className="grid items-center gap-8 sm:grid-cols-[0.9fr_1.1fr]">
            <img
              src={IMAGES.doctorConsult}
              alt="Médecin expliquant avec bienveillance un traitement à sa patiente"
              className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-lg"
              loading="lazy"
              data-testid="maladie-image"
            />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
                Vous ou un proche venez d'être diagnostiqué ?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Recevoir un diagnostic de maladie rare bouleverse. Vous n'êtes pas seul :
                notre association vous écoute, répond à vos questions et vous met en relation
                avec d'autres familles qui vivent la même chose.
              </p>
              <Link to="/contact" data-testid="maladie-contact-button" className="btn-don mt-7">
                Nous contacter <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </Reveal>

        <div className="mt-14">
          <Disclaimer />
        </div>
      </div>
    </section>
  </div>
);

export default Maladie;
